import { useState, useEffect } from "react"
import { IconButton, InputAdornment, TextField, FormControlLabel, Checkbox, Typography, Divider, Button, Box, Grid } from "@mui/material"
import { Visibility, VisibilityOff, InfoOutlined } from "@mui/icons-material"
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { useSelector } from "react-redux"
import { useAppData } from "../../redux/appDataProvider"
import ApiClient from "../../utilities/apiClient"
import AppConfig from "../../AppConfig"

const NewContactFields = ({ onNext, fieldType, setNewContactValue, onClose }) => {
    const { userInfo, setUserInfo } = useAppData()
    const apiToken = useSelector(state => state.auth.apiToken)

    const [oldValue, setOldValue] = useState("")
    const [newValue, setNewValue] = useState("")
    const [confirmValue, setConfirmValue] = useState("")
    const [showOldValue, setShowOldValue] = useState(true)
    const [showNewValue, setShowNewValue] = useState(true)
    const [error, setError] = useState("")
    const [agreed, setAgreed] = useState(false)

    const label =
        fieldType === "email" ? "New Email Address" : "New Mobile Number"

    const placeholder =
        fieldType === "email" ? "Enter your New Email Address" : "Enter your New Mobile Number"

    const validate = () => {
        if (!oldValue || !newValue || !confirmValue) {
            setError("Please fill all fields.")
            return false
        }

        if (oldValue !== (fieldType === "email" ? userInfo?.CLIENT_ID_MAIL : userInfo?.MOBILE_NO)) {
            setError(`Old ${fieldType === 'email' ? 'email' : 'mobile number'} does not match our records.`)
            return false;
        }

        if (oldValue === newValue) {
            setError("Old and New Value should not be same.")
            return false
        }

        if (newValue !== confirmValue) {
            setError(
                fieldType === 'email'
                    ? "Email address does not match"
                    : "Phone numbers do not match"
            )
            return false
        }

        if (fieldType === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
            setError("Please enter a valid email address")
            return false
        }

        if (fieldType === "mobile" && !/^\d{10}$/.test(newValue)) {
            setError("Please enter a valid 10-digit mobile number.")
            return false
        }

        setError("")
        return true
    }

    const handleNewValueSubmit = () => {
        if (validate()) {

            const payload = {
                clientId: userInfo.CLIENT_ID,
                newValue
            }

            try {

                const apiClient = new ApiClient(apiToken)

                apiClient
                    .fetchData(AppConfig.api.Profile.sendContactOtp, "POST", payload)
                    .then((response) => {
                        if (!response) {
                            console.error("Error: Response is missing.")
                            setError("Error in Send OTP. Please try later.")
                            return
                        }
                        
                        if (response.status === 200 && response.data.success) {
                            onNext()
                        } else if ((response.status === 400 || response.status === 404 || response.status === 500) && !response.data.success) {
                            setError(response.data.message)
                            console.log("Error:", response.data?.message)
                        } else {
                            console.warn("Unexpected response status:", response.status)
                        }

                    })
                    .catch((error) => {
                        console.error("Error during API request (Promise rejection):", error)
                        setError("Error in Send OTP. Please try later.")
                    })
            } catch (error) {
                console.error("Error during API request (Try block) :", error)
                setError("Error in Send OTP. Please try later.")
            }

        }
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flexGrow: 1 }}>

                <Grid container alignItems="center" justifyContent="space-between">
                          <Grid item>
                            <Typography sx={{ fontSize: {xs: '14px', sm: '16px'}, fontWeight: 600, mb: 1 }}>
                    Add New {fieldType === 'email' ? 'Email Address' : 'Mobile Number'}
                </Typography>
                          </Grid>
                          <Grid item>
                            <IconButton onClick={onClose}>
                              <HighlightOffIcon sx={{ color: '#F3D45E' }} />
                            </IconButton>
                          </Grid>
                        </Grid>

                <Divider sx={{ my: 2, borderColor: "#999" }} />

                <Typography sx={{ color: "#999", mb: 3, fontSize: {xs: '14px', sm: '16px'} }}>Usually takes few seconds</Typography>

                <Typography sx={{ fontSize: {xs: '14px', sm: '16px'}, fontWeight: 600, mb: 2 }}>
                    Let's register your New {fieldType === 'email' ? 'Email' : 'Number'}
                </Typography>


                <TextField
                    variant="standard"
                    label={`Old ${fieldType === 'email' ? 'Email Address' : 'Mobile Number'}`}
                    type="text"
                    value={oldValue}
                    onChange={(e) => {
                        const inputValue = e.target.value
                        if (fieldType !== 'email') {
                            const numericValue = inputValue.replace(/\D/g, '')
                            setOldValue(numericValue.trim())
                        } else {
                            setOldValue(inputValue.toLowerCase().trim())
                        }
                    }}
                    fullWidth
                    margin="normal"
                    placeholder={`Enter your current ${fieldType === 'email' ? 'email address' : 'mobile number'}`}
                    autoComplete="new-password"
                    name="oldField"
                    inputMode={fieldType === 'email' ? 'email' : 'numeric'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowOldValue((prev) => !prev)} edge="end">
                                    {showOldValue ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        inputProps: {
                            style: showOldValue
                                ? { WebkitTextSecurity: 'disc', letterSpacing: '1px' }
                                : {},
                        },
                    }}
                />

                <TextField
                    variant="standard"
                    label={label}
                    type="text"
                    value={newValue}
                    onChange={(e) => {
                        const inputValue = e.target.value
                        if (fieldType !== 'email') {
                            const numericValue = inputValue.replace(/\D/g, '')
                            setNewValue(numericValue.trim())
                            setNewContactValue(numericValue.trim())
                        } else {
                            setNewValue(inputValue.toLowerCase().trim())
                            setNewContactValue(inputValue.toLowerCase().trim())
                        }
                    }}
                    fullWidth
                    margin="normal"
                    placeholder={placeholder}
                    autoComplete="new-password"
                    name="newField"
                    inputMode={fieldType === 'email' ? 'email' : 'numeric'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewValue(prev => !prev)} edge="end">
                                    {showNewValue ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        inputProps: {
                            style: showNewValue
                                ? { WebkitTextSecurity: 'disc', letterSpacing: '1px' }
                                : {},
                        },
                    }}
                />

                <TextField
                    variant="standard"
                    label={`Re-enter ${label}`}
                    type="text"
                    value={confirmValue}
                    onChange={(e) => {
                        const inputValue = e.target.value
                        if (fieldType !== 'email') {
                            const numericValue = inputValue.replace(/\D/g, '')
                            setConfirmValue(numericValue.trim())
                        } else {
                            setConfirmValue(inputValue.toLowerCase().trim())
                        }
                    }}
                    fullWidth
                    margin="normal"
                    autoComplete="new-password"
                    name="renewField"
                    inputMode={fieldType === 'email' ? 'email' : 'numeric'}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowNewValue((prev) => !prev)} edge="end">
                                    {showNewValue ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                        inputProps: {
                            style: showNewValue
                                ? { WebkitTextSecurity: 'disc', letterSpacing: '1px' }
                                : {},
                        },
                    }}
                />


                {error && (
                    <Typography color="error" variant="body2" mt={1}>
                        {error}
                    </Typography>
                )}


                <Box display="flex" alignItems="center" mt={2} mb={2}>
                    <InfoOutlined fontSize="small" sx={{ color: "#888", mr: 1 }} />
                    <Typography sx={{ fontSize: {xs: '12px', sm: '13px'}, color: "#888" }}>
                        We will send you a one-time password (OTP) to verify your new {fieldType === "email" ? "Email" : "Mobile Number"}.
                    </Typography>
                </Box>

            </Box>

            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.5, mb: 0.5 }}>
                            I hereby declare that the Email / Mobile Number being updated here belongs to me. Further, I authorise Triveni Management Pvt. Ltd. to use this to send me any information/alert/email. This change will affect the Trading and Demat account that I hold with Triveni Management Pvt. Ltd.
                        </Typography>
                    }
                />

                <Button
                    onClick={handleNewValueSubmit}
                    fullWidth
                    disabled={!agreed}
                    sx={{ background: !agreed && "#999 !important" }}
                >
                    Send OTP
                </Button>
            </Box>
        </Box>
    )
}

export default NewContactFields
